/*
 * Description:
 * Handles rendering and logic for subtasks within a project task, including cost, profit, and sale breakdowns.
 * Provides add, edit, and remove functionality, profit margin handling, and integrates with Redux for state management.
 *
 * Features:
 * - Add, edit, and remove subtasks for a task
 * - Cost, profit, and sale calculation and display
 * - Profit margin input and validation
 * - Integration with Redux for subtask updates
 * - Uses Material UI, Bootstrap Table, and custom input components
 *
 */
import React, { useState, useEffect, useMemo } from "react";
import Table from "react-bootstrap/Table";
import Collapse from "react-bootstrap/Collapse";
import "../scss/task.scss";
import idx from "idx";
import { updateItemAction, updateSubTaskAction } from "../../../slice/add-task";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { numberFormat, toPercent, toUSD } from "../../../util/currency-formatter";
import { subTaskCalculation } from "../../../util/calculations/calculate-sub-task";
import Material from "./material";
import Labor from "./labor";
import Subcontractor from "./subcontractor";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { subcontractorObjFields } from "../../../constants/fields.constant";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TextAreaInput from "../../../components/inputs/textarea-input";
import {
  ExpandLessOutlined,
  ExpandMoreOutlined,
  SwapHoriz,
} from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import AddIcon from "@mui/icons-material/Add";
import TaskTemplate from "./taskTemplate";
import { updateLaborSubtask } from "./binders/labor";
import { updateMaterialSubTask } from "./binders/material";
import { ValidateField, ValidatorTypes } from "../../../util/validators";
import { getItemTemplates } from "../../../actions/template-management.action";
import { getFormatedUnit } from "../../../util";
import DeleteDialog from "../../../components/delete-dialog";

export default function SubTask({
  subTaskArray = [],
  visibleItemIds = null,
  profitMargin,
  profitMarginType,
  isDisabled,
  handleSaveTemplateDialog,
  deleteSubTaskHandler,
  getDeleteSubTask,
  categoryList,
  unitList,
  isVariation = false,
  editMode = false,
  enableEditBtn = false,
  ...props
}) {
  const dispatch = useDispatch();
  const [expandSubTask, setExpandSubTask] = useState([]);

  // Filter items if visibleItemIds is provided (for hierarchy display)
  // Use useMemo to prevent creating new array reference on every render
  const itemsToRender = useMemo(() => {
    return visibleItemIds
      ? subTaskArray.filter((item) => visibleItemIds.includes(item.id))
      : subTaskArray;
  }, [subTaskArray, visibleItemIds]);

  useEffect(() => {
    getUnits();
  }, []);

  const [descriptionFields, setDescriptionFields] = useState({
    name: "description",
    label: "Description",
    value: "",
    disabled: isDisabled,
    isHTClassRequired: false,
    rows: "",
    error: "",
    className: "m-0",
    validation: [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA],
  });

  const getUnits = async () => {
    let units = unitList;
    const allUnits =
      (units && Array.isArray(units) && units.length > 0 && units[0].units) ||
      [];
    units = getFormatedUnit(allUnits);
    return units;
  };

  const inputChangeHandler = (e, index = "") => {
    const { name, value, type } = e.target;

    let _temp = _.cloneDeep(subTaskArray);
    let fieldValue = value;

    if (name === "description") {
      fieldValue = value;
      const validation = [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA];
      const { valid, error } = ValidateField({ value: fieldValue, validation });

      if (_temp[index]) {
        _temp[index].description = fieldValue;
        _temp[index].descriptionError = error;
        _temp[index].descriptionValid = valid;
      } else {
        _temp[index] = {
          description: fieldValue,
          descriptionError: error,
          descriptionValid: valid,
        };
      }
    } else if (type === "checkbox") {
      _temp[index]["is_actual_materialcost"] = e.target.checked;
    } else {
      _temp[index][name] = value;
    }
    _temp[index]["isedited"] = true;
    dispatch(updateSubTaskAction(_temp));
  };

  const { id, open, index, type, currentId, description } = getDeleteSubTask();
  const removeSubtask = () => {
    let _temp = _.cloneDeep(subTaskArray);
    _temp.splice(index, 1);
    dispatch(updateSubTaskAction(_temp));
  };

  const handleAddMaterial = (index) => {
    let _temp = _.cloneDeep(subTaskArray);
    _temp[index]["newMaterial"]
      ? _temp[index]["newMaterial"].push({ id: uuidv4() })
      : (_temp[index]["newMaterial"] = [{ id: uuidv4() }]);
    dispatch(
      updateItemAction({ material: _temp[index]["newMaterial"], index })
    );
    addSubTaskExpandIndex(index);
  };
  const handleAddLabor = (index) => {
    let _temp = _.cloneDeep(subTaskArray);
    _temp[index]["newLabor"]
      ? _temp[index]["newLabor"].push({ id: uuidv4() })
      : (_temp[index]["newLabor"] = [{ id: uuidv4() }]);
    dispatch(updateItemAction({ labor: _temp[index]["newLabor"], index }));
    addSubTaskExpandIndex(index);
  };
  const handleAddContractor = (index) => {
    let _temp = _.cloneDeep(subTaskArray);
    const newContractor = _.cloneDeep(subcontractorObjFields);
    _temp[index]["subcontractor"].push(newContractor);
    dispatch(updateSubTaskAction(_temp));
    addSubTaskExpandIndex(index);
  };

  const refreshHandler = async (items) => {
    let _temp = _.cloneDeep(subTaskArray);

    const updatedSubtask = await Promise.all(
      _temp.map(async (subTask) => {
        const itemToUpdate = items.find(
          (item) =>
            item.templateId === subTask.templateId &&
            item.id === subTask.id &&
            item.description === subTask.description
        );
        if (itemToUpdate) {
          try {
            const response = await dispatch(
              getItemTemplates({ id: itemToUpdate.templateId })
            );
            const template = response;

            if (template) {
              const templateMaterials = Array.isArray(template.material)
                ? template.material.map((m) =>
                  typeof m === "string" ? JSON.parse(m) : m
                )
                : [];
              const templateLabors = Array.isArray(template.labor)
                ? template.labor.map((l) =>
                  typeof l === "string" ? JSON.parse(l) : l
                )
                : [];

              subTask.labor = updateLaborSubtask(
                subTask,
                templateLabors,
                itemToUpdate
              );
              subTask.material = updateMaterialSubTask(
                subTask,
                templateMaterials,
                itemToUpdate
              );
            }

            subTask.templateQuantity = itemToUpdate.templateQuantity;
          } catch (error) {
            console.error(
              `Error fetching item templates for templateId: ${itemToUpdate.templateId}`,
              error
            );
          }
          return subTask;
        } else {
          return subTask;
        }
      })
    );

    dispatch(updateSubTaskAction(updatedSubtask));
  };

  const deleteHandler = async (id) => {
    removeSubtask();
    deleteSubTaskHandler({
      subtaskIndex: index,
      subTaskId: id,
      description: description,
    });
  };

  const updateDeleteSubTask = () => {
    const subTaskId = id.filter((id) => id !== currentId);
    deleteSubTaskHandler({ subTaskId, description: description });
  };

  const getUniqSubTaskId = (oldIds, newId) => {
    return [...new Set([...oldIds, newId])];
  };

  const addSubTaskExpandIndex = (subtaskIndex) => {
    setExpandSubTask((prev) =>
      prev.includes(subtaskIndex) ? prev : [...prev, subtaskIndex]
    );
  };

  const removeSubTaskExpandIndex = (subtaskIndex) => {
    setExpandSubTask((prev) => prev.filter((index) => index !== subtaskIndex));
  };

  useEffect(() => {
    setDescriptionFields(
      itemsToRender.map((subtask) => ({
        name: "description",
        label: "Description",
        value: subtask.description || "",
        disabled: isDisabled,
        isHTClassRequired: false,
        // rows: subtask.descriptionError ? 2 : 5,
        error: subtask.descriptionError || "",
        className: "m-0 variation-description-textarea",
        validation: [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA],
      }))
    );
  }, [itemsToRender, isDisabled]);

  return (
    <>
      {open && (
        <DeleteDialog
          open={open}
          id={id}
          deleteHandler={() => deleteHandler(id)}
          dialogClose={() => updateDeleteSubTask()}
          moduleName={type}
          description={`${type} ${description}`}
        />
      )}
      {itemsToRender.map((subtask, renderIndex) => {
        // Get the REAL index from the full subTaskArray for state management
        const subtaskIndex = subTaskArray.findIndex(
          (st) =>
            (st.id && st.id === subtask.id) ||
            (st.mapId && st.mapId === subtask.mapId) // mapId is a client-side UUID used as a stable key for subtasks that do not yet have a persisted backend id
        );
        // Use renderIndex for accessing descriptionFields which is mapped from itemsToRender
        const descriptionIndex = renderIndex;

        let _total = subTaskCalculation({
          subtask: subtask,
          profitMargin: profitMargin,
          profitMarginType: profitMarginType,
          isForm: true,
        });

        return (
          <div
            key={subtask.id || subtask.mapId}
            className={`border-primary-app create-table-block mb-2 pb-0 variation-table-subtask px-0 shadow-sm${isVariation ? " variation-table-block" : " variation-table-block"
              }`}
            data-cy="subtask"
          >
            {isVariation ? (
              <div className="variation-summary-container row">
                <div
                  className="Variation-description col-lg-4 col-md-12 col-sm-12"
                  style={{
                    margin: "0 !important",
                    marginTop: "2px !important",
                  }}
                >
                  <div className="row">
                    <div className="col-lg-11 col-md-11 col-sm-11">
                      <TextAreaInput
                        {...descriptionFields[descriptionIndex]}
                        data-cy="description-input"
                        onChange={(e) => {
                          inputChangeHandler(e, subtaskIndex);
                          setDescriptionFields((prevDescriptionFields) => {
                            const updatedFields = [...prevDescriptionFields];
                            updatedFields[descriptionIndex] = {
                              ...updatedFields[descriptionIndex],
                              value: e.target.value,
                              rows:
                                e.target.value &&
                                  e.target.value.trim().length >= 2
                                  ? 3
                                  : 2,
                            };
                            return updatedFields;
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                {/* <div className="col-lg-1 col-md-1 col-sm-1"></div> */}
                <div className="table-responsive Variation-summary-table variation-table-subtask-header col-lg-7 col-md-11 col-sm-11">
                  <Table
                    bordered
                    className={`table-create mb-0 tbl-fixed tbl-fixed-width variation-table-subtask`}
                  >
                    <tbody>
                      <tr className={"border-top-0"}>
                        <th></th>
                        <th>
                          <strong>Material</strong>
                        </th>
                        <th>
                          <strong>Contractor</strong>
                        </th>
                        <th>
                          <strong>Resource</strong>
                        </th>
                        <th>
                          <strong>Planned Effort</strong>
                        </th>
                        <th>
                          <strong>Total</strong>
                        </th>
                      </tr>
                      <tr>
                        <td className="fw-bold">Cost</td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.material.cost))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.subcontractor.cost))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.labor.cost))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {numberFormat(idx(_total, (_) => _.forecastHours) || 0, true)} hrs
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.subtotal))}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">
                          <strong>Profit</strong>
                        </td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.material.profit),
                                idx(_total, (_) => _.material.cost)
                              )}
                              ) {toUSD(idx(_total, (_) => _.material.profit))}
                            </div>
                          </span>
                        </td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.subcontractor.profit),
                                idx(_total, (_) => _.subcontractor.cost)
                              )}
                              ){" "}
                              {toUSD(
                                idx(_total, (_) => _.subcontractor.profit)
                              )}
                            </div>
                          </span>
                        </td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.labor.profit),
                                idx(_total, (_) => _.labor.cost)
                              )}
                              ) {toUSD(idx(_total, (_) => _.labor.profit))}
                            </div>
                          </span>
                        </td>
                        <td></td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.total_profit),
                                idx(_total, (_) => _.subtotal)
                              )}
                              ) {toUSD(idx(_total, (_) => _.total_profit))}
                            </div>
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Sale</td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.material.sale))}
                          </strong>
                        </td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.subcontractor.sale))}
                          </strong>
                        </td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.labor.sale))}
                          </strong>
                        </td>
                        <td></td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.total))}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
                <div className="table-responsive Variation-summary-table col-lg-1 col-md-1 col-sm-1">
                  <div
                    className="ps-2 pe-3 py-0 d-flex justify-content-center"
                    rowSpan={4}
                  >
                    {!expandSubTask.includes(subtaskIndex) && (
                      <span
                        className="d-flex justify-content-center"
                        title={"Expand"}
                        data-cy="collapse-toggle"
                      >
                        <ExpandMoreOutlined
                          fontSize="large"
                          onClick={() => addSubTaskExpandIndex(subtaskIndex)}
                        />
                      </span>
                    )}
                    {expandSubTask.includes(subtaskIndex) && (
                      <span
                        className="d-flex justify-content-center"
                        title={"Minimize"}
                        data-cy="collapse-toggle"
                      >
                        <ExpandLessOutlined
                          fontSize="large"
                          onClick={() => removeSubTaskExpandIndex(subtaskIndex)}
                        />
                      </span>
                    )}
                  </div>
                  <div
                    className="d-flex flex-wrap gap-2 justify-content-center variation-action-btn"
                    style={{ maxWidth: "80px" }}
                  >
                    {props.wTemplatePermission && (
                      <div
                        title="Save as Item Template"
                        onClick={() =>
                          handleSaveTemplateDialog("subtask", subtask)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <SaveIcon className="fs-4 save-icon" />
                      </div>
                    )}
                    {/*{editMode && props.onCopyItem && (
                      <div
                        title="Copy Item to Section"
                        onClick={() => props.onCopyItem(subtask)}
                        style={{ cursor: "pointer" }}
                        data-cy="copy-item-btn"
                      >
                        <ContentCopyIcon className="fs-4 copy-icon" />
                      </div>
                    )}
                    {editMode && props.onMoveItem && (
                      <div
                        title="Move Item to Section"
                        onClick={() => props.onMoveItem(subtask)}
                        style={{ cursor: "pointer" }}
                        data-cy="move-item-btn"
                      >
                        <SwapHoriz className="fs-4 swap-icon" />
                      </div>
                    )} */}
                    {!isDisabled && (
                      <div
                        title="Delete Item Template"
                        onClick={() =>
                          deleteSubTaskHandler({
                            subtaskIndex,
                            subTaskId: subtask.id
                              ? getUniqSubTaskId(id, subtask.id)
                              : [],
                            type: "Item",
                            currentId: subtask.id || "",
                            description: subtask.description,
                          })
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <DeleteIcon className="delete-icon fs-4" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="variation-summary-container row">
                <div className="Variation-description col-lg-4 col-md-12 col-sm-12"
                  style={{
                    margin: "0 !important",
                    marginTop: "2px !important",
                  }}
                >
                  <div className='row'>
                    <div className="col-lg-11 col-md-11 col-sm-11">
                      <TextAreaInput
                        {...descriptionFields[descriptionIndex]}
                        data-cy="input"
                        onChange={(e) => {
                          inputChangeHandler(e, subtaskIndex)
                          setDescriptionFields((prevDescriptionFields) => {
                            const updatedFields = [...prevDescriptionFields]
                            updatedFields[descriptionIndex] = {
                              ...updatedFields[descriptionIndex],
                              value: e.target.value,
                              rows: e.target.value && e.target.value.trim().length >= 2 ? 3 : 2
                            }
                            return updatedFields
                          })
                        }}
                      />
                    </div>
                  </div>
                </div>
                {/** Table */}
                  <div className="table-responsive Variation-summary-table variation-table-subtask-header col-lg-7 col-md-11 col-sm-11">
                    <Table bordered
                      className={`table-create mb-0 tbl-fixed tbl-fixed-width variation-table-subtask`}>
                    <tbody>
                      <tr className={"border-top-0"}>
                        <th></th>
                        <th>
                          <strong>Material</strong>
                        </th>
                        <th>
                          <strong>Contractor</strong>
                        </th>
                        <th>
                          <strong>Resource</strong>
                        </th>
                        <th>
                          <strong>Planned Effort</strong>
                        </th>
                        <th>
                          <strong>Total</strong>
                        </th>
                      </tr>
                      <tr>
                        <td className="fw-bold">Cost</td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.material.cost))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.subcontractor.cost))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.labor.cost))}
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {numberFormat(idx(_total, (_) => _.forecastHours) || 0, true)} hrs
                          </span>
                        </td>
                        <td>
                          <span className="tableinfo">
                            {toUSD(idx(_total, (_) => _.subtotal))}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">
                          <strong>Profit</strong>
                        </td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.material.profit),
                                idx(_total, (_) => _.material.cost)
                              )}
                              ) {toUSD(idx(_total, (_) => _.material.profit))}
                            </div>
                          </span>
                        </td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.subcontractor.profit),
                                idx(_total, (_) => _.subcontractor.cost)
                              )}
                              ){" "}
                              {toUSD(
                                idx(_total, (_) => _.subcontractor.profit)
                              )}
                            </div>
                          </span>
                        </td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.labor.profit),
                                idx(_total, (_) => _.labor.cost)
                              )}
                              ) {toUSD(idx(_total, (_) => _.labor.profit))}
                            </div>
                          </span>
                        </td>
                        <td></td>
                        <td>
                          <span>
                            <div>
                              (
                              {toPercent(
                                idx(_total, (_) => _.total_profit),
                                idx(_total, (_) => _.subtotal)
                              )}
                              ) {toUSD(idx(_total, (_) => _.total_profit))}
                            </div>
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Sale</td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.material.sale))}
                          </strong>
                        </td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.subcontractor.sale))}
                          </strong>
                        </td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.labor.sale))}
                          </strong>
                        </td>
                        <td></td>
                        <td>
                          <strong className="tableinfo">
                            {toUSD(idx(_total, (_) => _.total))}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
                {/** Actions */}
                <div className="table-responsive Variation-summary-table col-lg-1 col-md-1 col-sm-1">
                  <div className="ps-2 pe-3 py-0 d-flex justify-content-center" rowSpan={4}>
                      {!expandSubTask.includes(subtaskIndex) && (
                        <span
                          className="d-flex justify-content-center"
                          title={"Expand"}
                          data-cy="collapse-toggle"
                        >
                          <ExpandMoreOutlined
                            fontSize="large"
                            onClick={() => addSubTaskExpandIndex(subtaskIndex)}
                          />
                        </span>
                      )}
                      {expandSubTask.includes(subtaskIndex) && (
                        <span
                          className="d-flex justify-content-center"
                          title={"Minimize"}
                          data-cy="collapse-toggle"
                        >
                          <ExpandLessOutlined
                            fontSize="large"
                            onClick={() => removeSubTaskExpandIndex(subtaskIndex)}
                          />
                        </span>
                      )}
                  </div>
                    <div
                      className="d-flex flex-wrap gap-2 justify-content-center variation-action-btn"
                      style={{ maxWidth: "80px" }}
                    >
                      {props.wTemplatePermission && (
                        <div
                          title="Save as Item Template"
                          onClick={() =>
                            handleSaveTemplateDialog("subtask", subtask)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <SaveIcon className="fs-4 save-icon" />
                        </div>
                      )}
                      {/*{editMode && props.onCopyItem && (
                      <div
                        title="Copy Item to Section"
                        onClick={() => props.onCopyItem(subtask)}
                        style={{ cursor: "pointer" }}
                        data-cy="copy-item-btn"
                      >
                        <ContentCopyIcon className="fs-4 copy-icon" />
                      </div>
                    )}
                    {editMode && props.onMoveItem && (
                      <div
                        title="Move Item to Section"
                        onClick={() => props.onMoveItem(subtask)}
                        style={{ cursor: "pointer" }}
                        data-cy="move-item-btn"
                      >
                        <SwapHoriz className="fs-4 swap-icon" />
                      </div>
                    )} */}
                      {!isDisabled && (
                        <div
                          title="Delete Item Template"
                          onClick={() =>
                            deleteSubTaskHandler({
                              subtaskIndex,
                              subTaskId: subtask.id
                                ? getUniqSubTaskId(id, subtask.id)
                                : [],
                              type: "Item",
                              currentId: subtask.id || "",
                              description: subtask.description,
                            })
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <DeleteIcon className="delete-icon fs-4" />
                        </div>
                      )}
                    </div>
                </div>
              </div>
            )}
            <div className="py-1 px-3 ps-2 w-100" data-cy="quantity-container">
              <div className="d-flex justify-content-between add-btns variation-add-btns mb-3 mt-2">
                {subtask.templateId &&
                  subtask.templateQuantity != null &&
                  subtask.templateQuantity > 0 &&
                  subtask.templateName != null ? (
                  <TaskTemplate
                    templateId={subtask.templateId || subtask.id}
                    templateName={subtask.templateName || subtask.description}
                    selectedQty={
                      subtask.templateQuantity || subtask.selectedQty
                    }
                    item={subtask}
                    refreshHandler={refreshHandler}
                    editMode={editMode}
                    enableEditBtn={enableEditBtn}
                    isTaskTemplateQty={false}
                    isVariation={isVariation}
                  />
                ) : (
                  <div></div>
                )}
                <div className="d-flex gap-3">
                  {!isDisabled && (
                    <span
                      className="tbl-action-btn"
                      data-cy="add-material"
                      onClick={() =>
                        !isDisabled && handleAddMaterial(subtaskIndex)
                      }
                    >
                      <AddIcon /> Add Material
                    </span>
                  )}
                  {!isDisabled && (
                    <span
                      className="tbl-action-btn"
                      data-cy="add-contractor"
                      onClick={() =>
                        !isDisabled && handleAddContractor(subtaskIndex)
                      }
                    >
                      <AddIcon /> Add Contractor
                    </span>
                  )}
                  {!isDisabled && (
                    <span
                      className="tbl-action-btn"
                      data-cy="add-resource"
                      onClick={() =>
                        !isDisabled && handleAddLabor(subtaskIndex)
                      }
                    >
                      <AddIcon /> Add Resource
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Collapse in={expandSubTask.includes(subtaskIndex)}>
              <div className="col-12">
                <div className="px-1">
                  <Material
                    material={subtask.material || []}
                    newMaterial={subtask["newMaterial"] || []}
                    subTaskArray={subTaskArray}
                    subTaskArrayIndex={subtaskIndex}
                    profitMargin={profitMargin}
                    profitMarginType={profitMarginType}
                    total={_total}
                    isDisabled={isDisabled}
                    isVariation={isVariation}
                    handleAddMaterial={handleAddMaterial}
                    minimizeHandler={() => expandSubTask.includes(subtaskIndex)}
                    categoryList={categoryList}
                    unitList={getUnits}
                  />
                </div>
                <div className="px-1">
                  <Labor
                    labor={subtask.labor || []}
                    laborOptions={props.laborsList || []}
                    newLabor={subtask["newLabor"] || []}
                    subTaskArray={subTaskArray}
                    subTaskArrayIndex={subtaskIndex}
                    total={_total}
                    isDisabled={isDisabled}
                    isVariation={isVariation}
                    handleAddLabor={handleAddLabor}
                    minimizeHandler={() => expandSubTask.includes(subtaskIndex)}
                  />
                </div>
                <div className="px-1">
                  <Subcontractor
                    subcontractor={subtask.subcontractor || []}
                    newContractor={subtask["newContractor"] || []}
                    subcontractorOptions={props.subcontractors || []}
                    profitMargin={profitMargin}
                    profitMarginType={profitMarginType}
                    subTaskArray={subTaskArray}
                    subTaskArrayIndex={subtaskIndex}
                    total={_total}
                    isDisabled={isDisabled}
                    handleAddContractor={handleAddContractor}
                    minimizeHandler={() => expandSubTask.includes(subtaskIndex)}
                  />
                </div>
              </div>
            </Collapse>
          </div>
        );
      })}
    </>
  );
}
